import { TenantAccessError, type NotificationRepository, type TenantContext } from '@zetyar/data-contracts'
import type { Notification, UUID } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

export function createSupabaseNotificationRepository(client: SupabaseClient): NotificationRepository {
  return {
    async list(context: TenantContext, limit: number) {
      if (!context.membershipId) return []

      const { data, error } = await client
        .from('notifications')
        .select('id, organization_id, membership_id, type, title, body, target_type, target_id, is_read, created_at')
        .eq('organization_id', context.organizationId)
        .eq('membership_id', context.membershipId)
        .order('created_at', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 100)))

      if (error) throw error

      return (data ?? []).map((item): Notification => ({
        id: item.id as UUID,
        organizationId: item.organization_id as UUID,
        membershipId: item.membership_id as UUID,
        type: item.type as string,
        title: item.title as string,
        body: (item.body as string | null) ?? null,
        targetType: (item.target_type as string | null) ?? null,
        targetId: (item.target_id as UUID | null) ?? null,
        isRead: item.is_read as boolean,
        createdAt: item.created_at as string,
      }))
    },

    async getUnreadCount(context: TenantContext) {
      if (!context.membershipId) return 0

      const { count, error } = await client
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', context.organizationId)
        .eq('membership_id', context.membershipId)
        .eq('is_read', false)

      if (error) throw error
      return count ?? 0
    },

    async markRead(context: TenantContext, notificationId: UUID) {
      if (!context.membershipId) throw new TenantAccessError('Membership is required to mark a notification as read')

      const { error } = await client
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId)
        .eq('membership_id', context.membershipId)

      if (error) throw error
    },
  }
}
